import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import config from "@Config/index";
import { AppState } from "@Store/index";
import useWhitelabel from "hooks/useWhitelabel";
import { getCurrencyFromRegion } from "@Components/LeadFormModal/utils";
import { PAGE } from "constants/configConstants";
import { selectPriceHistogram } from "@Pages/Search/data/searchPageReducer";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import getCookie from "helpers/getCookie";
import {
  getPricingCurrency,
  getPricingRangeFromRegion,
} from "@Pages/Search/components/SearchListItem/helper";
import { FilterComponentProps } from "../../ActiveFilterComponent/types";
import {
  getMaxAvailablePrice,
  getMinAvailablePrice,
  isMonthlyRegion,
  rebinOntoGrid,
  PriceFloorStats,
  PriceHistogram,
  PriceHistogramBucket,
} from "./budgetBounds";

export type { PriceFloorStats, PriceHistogram, PriceHistogramBucket };

export const snapToInterval = (
  value: number,
  min: number,
  max: number,
  interval: number,
  // Bucket edges sit on `histogram.min + n * interval`, which is not the floor. Snapping from the
  // floor instead would land every stop under the middle of a bar.
  gridOrigin: number = min,
) => {
  // Bounds are always valid stops; otherwise the top snaps down and the catch-all never fires.
  if (value >= max) return max;
  if (value <= min) return min;
  const step = interval || 1;
  const snapped = gridOrigin + Math.round((value - gridOrigin) / step) * step;
  return Math.min(Math.max(snapped, min), max);
};

const isUsableHistogram = (histogram?: PriceHistogram | null): histogram is PriceHistogram =>
  Boolean(
    histogram &&
      Number.isFinite(histogram.min) &&
      Number.isFinite(histogram.max) &&
      histogram.max > histogram.min,
  );

/** Bounds for when there is no histogram at all, so the filter still works. */
const getRegionBounds = (region: any) => {
  const isMonthlyCountry = isMonthlyRegion(region);
  const priceRange = region?.region_inventory_price_range;
  const [countryMin, countryMax] = getPricingRangeFromRegion(region);

  const regionMin = Number(
    isMonthlyCountry ? priceRange?.min_available_price : priceRange?.min_weekly_price,
  );
  const regionMax = Number(
    isMonthlyCountry ? priceRange?.max_available_price : priceRange?.max_weekly_price,
  );

  const min = Number.isFinite(regionMin) && regionMin >= 0 ? Math.floor(regionMin) : countryMin;
  const max =
    Number.isFinite(regionMax) && regionMax > min
      ? Math.ceil(regionMax)
      : Math.max(countryMax, min + 1);

  return { min, max, interval: 1 };
};

type RegionHistogram = { histogram: PriceHistogram; floor: number | null; ceiling: number | null };

/**
 * The geography the page is counting over — university, locality, near-by, radius — mirrored from
 * the filter-count request in `FilterSection`. Only the user's *filters* are dropped from the grid
 * request: its market has to be the one the page is showing, or the bars, the floor and the "+"
 * describe somewhere else entirely (a university page scoped back to its city, say).
 */
export const getHistogramScope = (region: any, filters: any) => {
  const university = filters?.university;
  const locality = filters?.locality;
  const scope: Record<string, any> = {
    region_type: "circle",
    region_radius:
      filters?.region_radius || (region?.location?.country?.short_name === "AE" ? 60000 : 20000),
  };
  if (locality && university) {
    scope.locality_canonical_name = locality;
    scope.region_canonical_name = university;
    scope.locality_radius = 10000;
  } else if (locality) {
    scope.locality_canonical_name = locality;
  } else {
    scope.region_canonical_name = university || region?.canonical_name;
  }
  if (typeof window !== "undefined" && String(locality ?? "").includes("near-by")) {
    const lat = getCookie("nearby_lat");
    const lng = getCookie("nearby_lng");
    if (lat && lng) {
      scope.isNearbyPage = true;
      scope.lat = parseFloat(lat);
      scope.lng = parseFloat(lng);
      scope.region_radius = 30000;
    }
  }
  return scope;
};

/** The geography params plus a key that changes whenever they do. */
const useHistogramScope = (region: any) => {
  const university = useSelector(
    ({ searchPageData }: AppState) => (searchPageData?.filters as any)?.university,
  );
  const locality = useSelector(
    ({ searchPageData }: AppState) => (searchPageData?.filters as any)?.locality,
  );
  const regionRadius = useSelector(
    ({ searchPageData }: AppState) => (searchPageData?.filters as any)?.region_radius,
  );
  const regionKey = region?.canonical_name ?? "";
  return useMemo(
    () => getHistogramScope(region, { university, locality, region_radius: regionRadius }),
    [regionKey, university, locality, regionRadius],
  );
};

/**
 * One unfiltered histogram per region *and* scope, shared by every panel instance: the fixed grid
 * the bars, the slider domain and the "+" are drawn on, so none of them move as the user's filters
 * narrow the results. Also stands in for the bars when the filtered count returns
 * `price_histogram: null`. Cosmetic only — the listing and the count are untouched.
 */
const regionHistogramRequests = new Map<string, Promise<RegionHistogram | null>>();

/** Module-level and never torn down, so it is capped. Oldest out first; a stale region refetches. */
const REGION_HISTOGRAM_CACHE_LIMIT = 8;

const fetchRegionHistogram = (
  region: any,
  scope: Record<string, any>,
  whitelabelPartnerId?: string | number | null,
) => {
  const duration = isMonthlyRegion(region) ? "monthly" : "weekly";
  const key = `${JSON.stringify(scope)}|${duration}|${whitelabelPartnerId ?? ""}`;
  if (!regionHistogramRequests.has(key)) {
    const request = axios
      .get(`${config.API_URL}/api/v1/inventories/inventory_filter_count`, {
        params: {
          status: "active",
          agg_keys: "price_stats,price_histogram",
          ...scope,
          use_v2_structure: true,
          price_histogram_duration: duration,
          use_java_api: true,
          ...(whitelabelPartnerId && { whitelabel_partner_id: whitelabelPartnerId }),
        },
      })
      .then((response): RegionHistogram | null => {
        const data = response?.data?.data?.data;
        const histogram = data?.price_histogram;
        return isUsableHistogram(histogram)
          ? {
              histogram,
              floor: getMinAvailablePrice(data, region),
              ceiling: getMaxAvailablePrice(data, region),
            }
          : null;
      })
      .catch(() => null);
    // Map iterates in insertion order, so the first key is the oldest.
    if (regionHistogramRequests.size >= REGION_HISTOGRAM_CACHE_LIMIT) {
      const oldest = regionHistogramRequests.keys().next().value;
      if (oldest !== undefined) regionHistogramRequests.delete(oldest);
    }
    regionHistogramRequests.set(key, request);
    // Failures resolve to null; caching that would disable the fallback for the session, since
    // `has(key)` never retries. Drop it so the next caller asks again.
    request.then((result) => {
      if (!result && regionHistogramRequests.get(key) === request) {
        regionHistogramRequests.delete(key);
      }
    });
  }
  return regionHistogramRequests.get(key) as Promise<RegionHistogram | null>;
};

type Args = {
  value?: string | string[];
  priceHistogram?: PriceHistogram | null;
  /** price_stats from the same count response; its minimum becomes the slider's floor. */
  priceStats?: PriceFloorStats | null;
  /** True while the filter-count request is in flight. Gates the region-wide fallback. */
  isLoading?: boolean;
  /**
   * Draw the bars on one region-wide histogram, with the filtered counts re-binned onto it, so the
   * number of bars and the price domain do not move as other filters change.
   */
  withStableGrid?: boolean;
  region?: any;
  dispatch: FilterComponentProps<string | string[]>["dispatch"];
  onApplyClick?: (payload: { budget: string[] }) => void;
  onResetClick?: (name: string) => void;
  /** `section` reported on the price_range_filter_applied event. */
  analyticsSection?: string;
};

const usePriceHistogram = ({
  value,
  priceHistogram,
  priceStats,
  isLoading = false,
  withStableGrid = false,
  region,
  dispatch,
  onApplyClick,
  onResetClick,
  analyticsSection = "Histogram Filter",
}: Args) => {
  const scope = useHistogramScope(region);
  // The grid belongs to a market, not just a region: switching university or locality is a
  // different set of properties and so a different set of bars.
  const regionKey = JSON.stringify(scope);
  const { isWhitelabel, isUniversityPartner, isRestrictedSearchEnabled, partner_id } =
    useWhitelabel();
  const whitelabelPartnerId =
    (isWhitelabel && isUniversityPartner) || isRestrictedSearchEnabled ? partner_id : null;

  /**
   * The count request clears `data` while in flight, so hold the last histogram to stop the bars
   * collapsing mid-refetch. Cleared on a region change so one region never shows another's prices.
   */
  const cache = useRef<{
    regionKey: string;
    histogram: PriceHistogram | null;
    floor: number | null;
    ceiling: number | null;
  }>({ regionKey, histogram: null, floor: null, ceiling: null });
  if (cache.current.regionKey !== regionKey) {
    cache.current = { regionKey, histogram: null, floor: null, ceiling: null };
  }
  if (isUsableHistogram(priceHistogram)) {
    cache.current.histogram = priceHistogram;
    // Floor and ceiling travel with the histogram they were aggregated with, so a refetch cannot
    // drop the minimum to the grid origin mid-drag.
    cache.current.floor = getMinAvailablePrice(priceStats, region);
    cache.current.ceiling = getMaxAvailablePrice(priceStats, region);
  }
  const liveHistogram = isUsableHistogram(priceHistogram)
    ? priceHistogram
    : cache.current.histogram;

  const [regionFallback, setRegionFallback] = useState<
    (RegionHistogram & { regionKey: string }) | null
  >(null);
  useEffect(() => {
    if (!withStableGrid || !region?.canonical_name) {
      return undefined;
    }
    let cancelled = false;
    fetchRegionHistogram(region, scope, whitelabelPartnerId).then((result) => {
      if (!cancelled && result) setRegionFallback({ ...result, regionKey });
    });
    return () => {
      cancelled = true;
    };
    // One request per region, shared and cached across every panel instance.
  }, [withStableGrid, regionKey, whitelabelPartnerId]);

  const regionGrid =
    regionFallback && regionFallback.regionKey === regionKey ? regionFallback : null;
  // The grid is the region's histogram where there is one: its buckets, its domain and its "+" are
  // what the panel is drawn on, whatever the current filters narrow the results to. The filtered
  // counts are re-binned onto it, so the bars keep their number and their positions and only their
  // heights move. Without a region grid (the inline panel) the live histogram is the grid, as before.
  const grid = regionGrid?.histogram ?? liveHistogram;
  const histogram = useMemo(() => {
    if (!grid) return null;
    if (!regionGrid || !liveHistogram) return grid;
    return { ...grid, ...rebinOntoGrid(liveHistogram, grid, regionGrid.ceiling) };
  }, [grid, regionGrid, liveHistogram]);
  const floor = regionGrid ? regionGrid.floor : cache.current.floor;
  const ceiling = regionGrid ? regionGrid.ceiling : cache.current.ceiling;

  const regionBounds = useMemo(() => getRegionBounds(region), [region]);

  // Start at the cheapest room, not the bucket grid's floor — but never past the top.
  const hasUsableFloor =
    histogram !== null && floor !== null && floor > histogram.min && floor < histogram.max;
  let { min } = regionBounds;
  if (histogram) min = hasUsableFloor ? (floor as number) : histogram.min;
  const interval = (histogram ? histogram.interval : regionBounds.interval) || 1;
  const max = histogram ? histogram.max : regionBounds.max;
  // What an open-ended budget ("£140+") is closed at on the way to the API.
  const openEndedCeiling = Math.max(ceiling ?? regionBounds.max, max);
  // Grid origin. The slider runs on it so stops land between bars, never under one. `min` is an
  // extra stop at the far left; the grid below it is unreachable.
  const gridMin = histogram ? histogram.min : regionBounds.min;
  const buckets = useMemo(() => histogram?.buckets ?? [], [histogram]);
  const catchAll = histogram?.catch_all;
  // The catch-all ("660+") gets an interval of its own at the top so its bar stays within the
  // handles' reach. Anywhere in that slot means "no upper bound".
  const topPrice = catchAll ? max + interval : max;
  // A range input only stops on `n * step` from its own minimum, so the track is measured from the
  // grid origin and rounded up to whole intervals; the ends map back onto the floor and `max`.
  const sliderMax = Math.ceil((topPrice - gridMin) / interval) * interval;
  const openEndedFrom = catchAll ? sliderMax - interval : sliderMax;
  const toSliderValue = (price: number) => {
    if (price >= max) return sliderMax;
    // The floor is usually off-grid; park it on the first stop so the browser does not re-snap it.
    if (price <= min) return 0;
    return price - gridMin;
  };
  const fromSliderValue = (position: number | string) => {
    if (Number(position) >= openEndedFrom) return max;
    return Math.max(Number(position) + gridMin, min);
  };
  const snap = (price: number) => snapToInterval(price, min, max, interval, gridMin);
  // `budget` arrives as ["0", "575"] here but as a comma string from the URL, so accept both.
  const applied = Array.isArray(value) ? value : String(value ?? "").split(",");

  /**
   * An applied price, or NaN when that end is unset. Not a bare `Number()`: an unset budget is
   * `[""]` and `Number("")` is 0, which would read as a real minimum of £0.
   */
  const appliedPrice = (raw: unknown) => {
    const text = String(raw ?? "").trim();
    return text === "" ? NaN : Number(text);
  };

  /**
   * Bounds for the selection, as opposed to `min`/`max`, which describe the bars.
   *
   * The histogram is recomputed per filter set, so an applied budget can fall outside it — add a
   * move-in month and London's bars go from 0-660 to 200-1000. Clamping into the new bounds put
   * both handles on the same edge ("£125 - £125"), so the clamp widens to cover what is applied.
   */
  const appliedLo = appliedPrice(applied[0]);
  const appliedHi = appliedPrice(applied[1]);
  const selectionMin = Number.isFinite(appliedLo) ? Math.min(min, appliedLo) : min;
  // Widened upwards only when the range starts above the bars. An applied maximum alone is not
  // enough: a budget that reached the catch-all is stored as the region's dearest price (3658 for
  // London) and must clamp back to `max` so the panel reads "£660+", not "£3658".
  const selectionMax =
    Number.isFinite(appliedLo) && appliedLo > max ? Math.max(max, appliedHi) : max;

  const clampToBounds = (price: number) => Math.min(Math.max(price, selectionMin), selectionMax);
  const currency = getPricingCurrency(
    histogram?.currency || getCurrencyFromRegion(region?.combined_name),
  );

  const fromAppliedValue = (): [number, number] => {
    const rawMin = appliedLo;
    const rawMax = appliedHi;
    // Clamped, not snapped: this re-reads the value just dispatched, and snapping here would pull
    // a typed 11 back to 20 and undo the entry.
    const nextMin = Number.isFinite(rawMin) ? clampToBounds(rawMin) : min;
    // A max at or above the top is open-ended; clamping lands the handle over the catch-all bar.
    const nextMax = Number.isFinite(rawMax) ? clampToBounds(rawMax) : max;
    return nextMin <= nextMax ? [nextMin, nextMax] : [min, max];
  };

  const [[selMin, selMax], setRange] = useState<[number, number]>(fromAppliedValue);
  const valueKey = `${applied[0] ?? ""}-${applied[1] ?? ""}`;
  const boundsKey = `${min}-${max}-${interval}`;
  // Re-derive the selection when the applied filter or the bounds change, without an effect —
  // an effect would render one frame with the stale selection and make the handles jump.
  const syncedOn = useRef(`${valueKey}|${boundsKey}`);
  if (syncedOn.current !== `${valueKey}|${boundsKey}`) {
    syncedOn.current = `${valueKey}|${boundsKey}`;
    const [nextMin, nextMax] = fromAppliedValue();
    if (nextMin !== selMin || nextMax !== selMax) setRange([nextMin, nextMax]);
  }

  const commitRange = (
    nextMin: number,
    nextMax: number,
    place: (price: number) => number = snap,
  ) => {
    const clampedMin = place(nextMin);
    const clampedMax = place(nextMax);
    const lo = Math.min(clampedMin, clampedMax);
    const hi = Math.max(clampedMin, clampedMax);
    setRange([lo, hi]);
    // Reaching the catch-all means "no maximum", which travels as the region's dearest price.
    const upper = hi >= max && catchAll ? openEndedCeiling : hi;
    // Spanning the whole range is not a filter — the empty default leaves Budget unselected.
    const payload =
      lo <= min && hi >= max ? [] : [String(Math.trunc(lo)), String(Math.trunc(upper))];
    dispatch({ type: "budget", payload });
    if (typeof onApplyClick === "function") onApplyClick({ budget: payload });
    gtagEventEmitter({
      action: "price_range_filter_applied",
      category: PAGE.SEARCH,
      min_price: lo,
      max_price: upper,
      section: analyticsSection,
    });
  };

  /** Handle and bar clicks move along the grid, so they keep snapping. */
  const apply = (nextMin: number, nextMax: number) => commitRange(nextMin, nextMax, snap);

  /**
   * Committed from the Minimum / Maximum boxes, kept exactly as typed within the bounds. The
   * handle still renders on the nearest grid stop, so it can sit slightly off the number shown.
   */
  const applyTyped = (nextMin: number, nextMax: number) =>
    commitRange(nextMin, nextMax, clampToBounds);

  const reset = () => {
    setRange([min, max]);
    if (typeof onResetClick === "function") onResetClick("Budget");
  };

  const onSliderChange = ([lo, hi]: [number, number]) => {
    setRange([fromSliderValue(lo), fromSliderValue(hi)]);
  };

  const onSliderDragEnd = (values?: [number, number]) => {
    const nextMin = values ? fromSliderValue(values[0]) : selMin;
    const nextMax = values ? fromSliderValue(values[1]) : selMax;
    apply(nextMin, nextMax);
  };

  /**
   * A click on a histogram bar moves the nearer handle onto that bucket, like clicking a slider
   * track: bars left of the selection widen it, bars inside it narrow it. The catch-all bar
   * (`lo === max`) pulls the maximum handle to the open-ended top.
   */
  const selectBucket = (lo: number, hi: number) => {
    const centre = (lo + hi) / 2;
    const moveMin = Math.abs(centre - selMin) < Math.abs(centre - selMax);
    if (moveMin) {
      apply(lo, selMax);
    } else {
      apply(selMin, hi);
    }
  };

  return {
    min,
    max,
    /** Bottom of the slider domain in price terms: the bucket grid's origin, at or below `min`. */
    gridMin,
    /** Top of the slider domain in price terms: `max`, plus one interval when there is a catch-all. */
    topPrice,
    interval,
    sliderMax,
    toSliderValue,
    buckets,
    catchAll,
    currency,
    selMin,
    selMax,
    apply,
    applyTyped,
    reset,
    onSliderChange,
    onSliderDragEnd,
    selectBucket,
    isDirty: selMin !== min || selMax !== max,
    hasHistogram: Boolean(histogram),
    /**
     * True when the bars on screen do not describe the current results — the count returned no
     * histogram, so they are either cached or region-wide. Keyed on the current response, not on
     * the fallback, because the cache would otherwise leave stale bars looking live.
     */
    isHistogramStale: Boolean(histogram) && !isLoading && !isUsableHistogram(priceHistogram),
  };
};

export default usePriceHistogram;

/**
 * The top of the current region's price histogram, for labels that need to spell out an
 * open-ended budget ("£40 - £660+"). The count request nulls the histogram while it is in
 * flight, so the last value seen for the region is held on to; it resets when the region
 * changes. `undefined` until a histogram has arrived — callers fall back to "£min+".
 */
export const useBudgetPriceCeiling = (): number | undefined => {
  const region = useSelector(({ searchPageData }: AppState) => searchPageData?.region);
  const scope = useHistogramScope(region);
  const regionKey = JSON.stringify(scope);
  const histogramMax = useSelector(
    (state: AppState) => selectPriceHistogram(state)?.max as number | undefined,
  );
  const { isWhitelabel, isUniversityPartner, isRestrictedSearchEnabled, partner_id } =
    useWhitelabel();
  const whitelabelPartnerId =
    (isWhitelabel && isUniversityPartner) || isRestrictedSearchEnabled ? partner_id : null;
  // `budget` is [] until one is applied, which is also what `getBudgetAppliedStatus` keys on.
  const hasAppliedBudget = useSelector(({ searchPageData }: AppState) =>
    Boolean((searchPageData?.filters as any)?.budget?.length),
  );

  const cache = useRef<{ regionKey: string; max?: number }>({ regionKey });
  if (cache.current.regionKey !== regionKey) {
    cache.current = { regionKey };
  }
  if (Number.isFinite(histogramMax)) {
    cache.current.max = histogramMax;
  }

  /**
   * The region-wide ceiling, resolved the same way the panel resolves it, through the same cached
   * request. Without this the pill and the panel name different ceilings once the filters match
   * nothing, because the panel falls back to the region range and this hook would not.
   */
  const [regionCeiling, setRegionCeiling] = useState<
    { regionKey: string; max: number } | undefined
  >();
  useEffect(() => {
    // Only when the ceiling will actually be read: it names the "+" on an applied budget, so
    // fetching without one added a request to every search page view.
    if (!regionKey || !hasAppliedBudget) {
      return undefined;
    }
    let cancelled = false;
    fetchRegionHistogram(region, scope, whitelabelPartnerId).then((result) => {
      if (!cancelled && result) setRegionCeiling({ regionKey, max: result.histogram.max });
    });
    return () => {
      cancelled = true;
    };
  }, [regionKey, whitelabelPartnerId, hasAppliedBudget]);

  // Stamped with its region: state does not reset on a region change, so an unstamped value would
  // print London's ceiling on a Manchester pill.
  const ceilingForRegion =
    regionCeiling && regionCeiling.regionKey === regionKey ? regionCeiling.max : undefined;

  // The region ceiling wins: it is the one the panel draws its "+" on, and unlike the filtered
  // histogram's max it does not move when another filter narrows the results. The live max only
  // stands in until that request resolves.
  if (ceilingForRegion !== undefined) return ceilingForRegion;
  if (Number.isFinite(histogramMax)) return histogramMax;
  return cache.current.max;
};
